import {
  type InfiniteData,
  isCancelledError,
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IS_MIX_CATALOG } from "@/config/browserBoard";
import {
  type Catalog,
  INFINITE_CATALOG_QUERY_KEY_PREFIX,
  type ThreadSummary,
} from "@/entities/thread";
import {
  createCatalogAutoReloadQueryBehavior,
  shouldRefreshCatalogOnlineUsers,
} from "@/features/catalog/hooks/catalogAutoReload";
import {
  getBoardCatalogPath,
  getCatalogMixPath,
  getCatalogPath,
} from "@/features/catalog/hooks/catalogPath";
import {
  type CatalogResponse,
  mergeCatalogPages,
  mergeViewerShadowThreads,
  normalizeCatalogResponse,
} from "@/features/catalog/hooks/catalogResponse";
import {
  CATALOG_PAGE_LIMIT_MAX,
  type SortType,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import {
  mergeMixCatalogs,
  planMixFetch,
} from "@/features/catalog/utils/mixMerge";
import { useMixCandidateSlugs } from "@/features/settings/hooks/useMixCandidateSlugs";
import {
  type MixSelection,
  resolveMixSelection,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { apiGet, hasAimgToken } from "@/shared/api/client";
import { refreshOnlineUsers } from "@/shared/api/onlineUsers";
import { useAimgViewerContext } from "@/shared/api/useAimgViewerContext";
import {
  getDocumentVisibilityState,
  useDocumentVisibilityState,
} from "@/shared/hooks/useDocumentVisibilityState";
import {
  runAimogeDataHook,
  useAimogeCatalogSnapshot,
  useAimogeHookGeneration,
} from "@/shared/lib/aimoge";
import { getApiErrorMessage } from "@/shared/ui/feedback/apiErrorMessage";

export {
  CATALOG_AUTO_RELOAD_INTERVAL,
  getCatalogRefetchInterval,
} from "@/features/catalog/hooks/catalogAutoReload";

/** カタログの共有pageをこのフックで上書きしたい呼び出し元向け(例: リストモードはページングをモード別に独立させ、グリッド設定と切り離した固定件数を使う)。 */
export interface UseThreadsOverrides {
  page?: number;
  limit?: number;
}

/** カタログqueryを表示モードに応じて休止するための購読設定。 */
export interface UseThreadsOptions {
  enabled?: boolean;
}

export interface ThreadsQueryParams {
  page: number;
  limit: number;
}

export type CatalogFetcherOptions = {
  allowTokenIssue?: boolean;
  /** 他板の板スコープAPIを引くためのX-Board-Key上書き(総合カタログ用)。 */
  boardKey?: string;
  cache?: RequestCache;
  requiresToken?: boolean;
  signal?: AbortSignal;
};

export type CatalogFetcher = <T>(
  path: string,
  options?: CatalogFetcherOptions,
) => Promise<T>;

interface FetchCatalogInput {
  catalogPath: string;
  fetcher: CatalogFetcher;
  hasViewerToken: boolean;
  limit: number;
  page: number;
  signal?: AbortSignal;
}

export function getThreadsQueryKey(
  catalogPath: string,
  viewerContext: number,
): readonly ["threads", string, "viewer", number] {
  return ["threads", catalogPath, "viewer", viewerContext];
}

/** SP版の全取得ページを1つのcacheとして識別するquery keyを返す。 */
export function getInfiniteThreadsQueryKey(
  catalogPath: string,
  viewerContext: number,
): readonly ["threads", "infinite", string, "viewer", number] {
  return [
    ...INFINITE_CATALOG_QUERY_KEY_PREFIX,
    catalogPath,
    "viewer",
    viewerContext,
  ];
}

export async function fetchCatalogWithViewerShadowThreads({
  catalogPath,
  fetcher,
  hasViewerToken,
  limit,
  page,
  signal,
}: FetchCatalogInput): Promise<Catalog> {
  const catalogRequest = fetcher<CatalogResponse>(catalogPath, { signal });
  if (!hasViewerToken || page !== 1) {
    return normalizeCatalogResponse(await catalogRequest, limit);
  }

  const [response, shadowThreads] = await Promise.all([
    catalogRequest,
    fetcher<ThreadSummary[]>("/shadow-threads", {
      allowTokenIssue: false,
      cache: "no-store",
      requiresToken: true,
      signal,
    }).catch((error: unknown) => {
      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      return [];
    }),
  ]);
  return mergeViewerShadowThreads(
    normalizeCatalogResponse(response, limit),
    shadowThreads,
  );
}

export interface FetchMixCatalogInput {
  sort: SortType;
  page: number;
  limit: number;
  selection: MixSelection;
  fetcher: CatalogFetcher;
  hasViewerToken: boolean;
  signal?: AbortSignal;
}

/**
 * 総合カタログ1ページ分を取得する。各板の枠数は`planMixFetch`で取得前に確定させ、
 * 制御なし群はcatalog-mix(サーバー側で板をまたいだ並び順を維持)を
 * 「ページ枠 − 取り分合計」+shadow thread合流つきで1回、%取り分の板は
 * /catalog/{slug}を各枠数で並列取得する。各板のoffsetがページ間で連続するため、
 * ページを送ってもスレの取りこぼしが起きない。取り分板の取得失敗は
 * その板抜きで縮退し、ページ全体は失敗させない。
 *
 * 全板に%上限を付けた構成では制御なし群が無くcatalog-mixを呼ばない
 * (空の`b=`はbackendが400で拒否する)。ページは取り分の合計までしか
 * 埋まらず、shadowスレ合流も効かない。
 */
export async function fetchMixCatalog(
  input: FetchMixCatalogInput,
): Promise<Catalog> {
  const { uncappedLimit, extras: plans } = planMixFetch(
    input.selection,
    input.limit,
  );
  const uncappedPromise =
    uncappedLimit === null
      ? null
      : fetchCatalogWithViewerShadowThreads({
          catalogPath: getCatalogMixPath(
            input.selection.uncappedSlugs,
            input.sort,
            input.page,
            uncappedLimit,
          ),
          fetcher: input.fetcher,
          hasViewerToken: input.hasViewerToken,
          limit: uncappedLimit,
          page: input.page,
          signal: input.signal,
        });
  const extraPromises = plans.map((plan) =>
    input
      .fetcher<CatalogResponse>(
        getBoardCatalogPath(plan.slug, input.sort, input.page, plan.limit),
        // 実backendはpath slugで板を解決しheaderを読まないが、boardKeyを
        // 対象板へ揃える規約(mix-catalog.md)としてE2Eモックが検査している
        { signal: input.signal, boardKey: plan.slug },
      )
      .then((response) => normalizeCatalogResponse(response, plan.limit))
      .catch((error: unknown): null => {
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }
        console.warn(`[mix] ${plan.slug}板のカタログ取得に失敗しました`, error);
        return null;
      }),
  );
  const [uncapped, extraCatalogs] = await Promise.all([
    uncappedPromise,
    Promise.all(extraPromises),
  ]);

  return mergeMixCatalogs({
    sort: input.sort,
    pageLimit: input.limit,
    page: input.page,
    uncapped:
      uncapped === null
        ? null
        : { slugs: input.selection.uncappedSlugs, catalog: uncapped },
    extras: plans.map((plan, index) => ({
      slug: plan.slug,
      catalog: extraCatalogs[index] ?? null,
    })),
  });
}

/** 総合カタログのquery key用path。板構成と取り分の変更で自然に再取得させる。 */
export function getMixCatalogKeyPath(
  sort: SortType,
  page: number,
  limit: number,
  selection: MixSelection,
): string {
  const uncapped = selection.uncappedSlugs.join("+");
  const extras = selection.extras
    .map((extra) => `${extra.slug}.${extra.pct}`)
    .join("+");
  return `mix:${uncapped}?sort=${sort}&page=${page}&limit=${limit}&extras=${extras}`;
}

/** storeのpage/columns/rowsとoverridesから実際に使うpage/limitを決定する純粋関数(テスト容易性のため分離)。 */
export function resolveThreadsQueryParams(
  store: { page: number; columns: number; rows: number },
  overrides?: UseThreadsOverrides,
): ThreadsQueryParams {
  return {
    page: overrides?.page ?? store.page,
    limit:
      overrides?.limit ??
      Math.max(1, Math.min(CATALOG_PAGE_LIMIT_MAX, store.columns * store.rows)),
  };
}

/** 拡張フックをカタログと各スレッドへ適用する安定した変換関数を返す。 */
function useTransformCatalog(): (catalog: Catalog) => Catalog {
  const aimogeGeneration = useAimogeHookGeneration();
  return useCallback(
    (catalog: Catalog): Catalog => {
      void aimogeGeneration;
      const transformedCatalog = runAimogeDataHook("data:catalog", catalog);
      return {
        ...transformedCatalog,
        threads: transformedCatalog.threads.map((thread) =>
          runAimogeDataHook("data:thread", thread),
        ),
      };
    },
    [aimogeGeneration],
  );
}

export function useThreads(
  overrides?: UseThreadsOverrides,
  options: UseThreadsOptions = {},
): UseQueryResult<Catalog> {
  const { currentSort, page, columns, rows, autoReload } = useCatalogStore();
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const mixSlugs = useMixCandidateSlugs();
  const boardRevision = useMixStore((state) => state.boardRevision);
  const { page: resolvedPage, limit } = resolveThreadsQueryParams(
    { page, columns, rows },
    overrides,
  );
  const mixSelection = IS_MIX_CATALOG
    ? (resolveMixSelection(mixSlugs, mixBoardSettings) ?? {
        uncappedSlugs: [],
        extras: [],
      })
    : null;
  const catalogPath = mixSelection
    ? getMixCatalogKeyPath(currentSort, resolvedPage, limit, mixSelection)
    : getCatalogPath(currentSort, resolvedPage, limit);
  const queryClient = useQueryClient();
  const viewerContext = useAimgViewerContext();
  const visibilityState = useDocumentVisibilityState();
  const lastFetchContextRef = useRef<string | null>(null);
  const fetchContext = `${catalogPath}\0${viewerContext}`;
  const transformCatalog = useTransformCatalog();

  const query = useQuery({
    queryKey: getThreadsQueryKey(
      IS_MIX_CATALOG ? `${catalogPath}&revision=${boardRevision}` : catalogPath,
      viewerContext,
    ),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const isInitialFetch = lastFetchContextRef.current !== fetchContext;
      const catalogPromise = mixSelection
        ? fetchMixCatalog({
            sort: currentSort,
            page: resolvedPage,
            limit,
            selection: mixSelection,
            fetcher: apiGet,
            hasViewerToken: hasAimgToken(),
            signal,
          })
        : fetchCatalogWithViewerShadowThreads({
            catalogPath,
            fetcher: apiGet,
            hasViewerToken: hasAimgToken(),
            limit,
            page: resolvedPage,
            signal,
          });
      if (
        (mixSelection === null ||
          mixSelection.uncappedSlugs.length + mixSelection.extras.length > 0) &&
        shouldRefreshCatalogOnlineUsers(
          autoReload,
          isInitialFetch,
          getDocumentVisibilityState(),
        )
      ) {
        void refreshOnlineUsers(queryClient);
      }
      const catalog = await catalogPromise;
      lastFetchContextRef.current = fetchContext;
      return catalog;
    },
    select: transformCatalog,
    ...createCatalogAutoReloadQueryBehavior(autoReload, () => visibilityState),
    enabled: options.enabled ?? true,
  });

  // 拡張向けsnapshot。selectを通した後の1本だけを公開するため、mixでは
  // 混合後の結果、個別板ではその板の結果になる(板ごとの内訳は出さない)。
  useAimogeCatalogSnapshot(options.enabled === false ? undefined : query.data);

  return query;
}

export type InfiniteThreadsQueryResult = UseQueryResult<Catalog> & {
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  nextPageError: string | null;
};

/** SP版カタログを1ページ目から順に追記取得する。 */
export function useInfiniteThreads(
  options: UseThreadsOptions = {},
): InfiniteThreadsQueryResult {
  const { currentSort, columns, rows, autoReload } = useCatalogStore();
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const mixSlugs = useMixCandidateSlugs();
  const boardRevision = useMixStore((state) => state.boardRevision);
  const limit = resolveThreadsQueryParams({
    page: 1,
    columns,
    rows,
  }).limit;
  const mixSelection = IS_MIX_CATALOG
    ? (resolveMixSelection(mixSlugs, mixBoardSettings) ?? {
        uncappedSlugs: [],
        extras: [],
      })
    : null;
  const catalogKeyPath = mixSelection
    ? getMixCatalogKeyPath(currentSort, 1, limit, mixSelection)
    : getCatalogPath(currentSort, 1, limit);
  const queryClient = useQueryClient();
  const viewerContext = useAimgViewerContext();
  const visibilityState = useDocumentVisibilityState();
  const lastFetchContextRef = useRef<string | null>(null);
  const isFetchingNextPageRef = useRef(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [nextPageError, setNextPageError] = useState<string | null>(null);
  const transformCatalog = useTransformCatalog();
  const mergePages = useCallback(
    (data: InfiniteData<Catalog, number>): Catalog =>
      transformCatalog(mergeCatalogPages(data.pages)),
    [transformCatalog],
  );

  const queryKey = useMemo(
    () =>
      getInfiniteThreadsQueryKey(
        IS_MIX_CATALOG
          ? `${catalogKeyPath}&revision=${boardRevision}`
          : catalogKeyPath,
        viewerContext,
      ),
    [catalogKeyPath, viewerContext, boardRevision],
  );
  const fetchPage = useCallback(
    (page: number, signal?: AbortSignal): Promise<Catalog> =>
      mixSelection
        ? fetchMixCatalog({
            sort: currentSort,
            page,
            limit,
            selection: mixSelection,
            fetcher: apiGet,
            hasViewerToken: hasAimgToken(),
            signal,
          })
        : fetchCatalogWithViewerShadowThreads({
            catalogPath: getCatalogPath(currentSort, page, limit),
            fetcher: apiGet,
            hasViewerToken: hasAimgToken(),
            limit,
            page,
            signal,
          }),
    [currentSort, limit, mixSelection],
  );

  const query = useQuery<
    InfiniteData<Catalog, number>,
    Error,
    Catalog,
    ReturnType<typeof getInfiniteThreadsQueryKey>
  >({
    queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const fetchContext = `${catalogKeyPath}\0${viewerContext}`;
      const isInitialFetch = lastFetchContextRef.current !== fetchContext;
      const catalogPromise = fetchPage(1, signal);
      if (
        (mixSelection === null ||
          mixSelection.uncappedSlugs.length + mixSelection.extras.length > 0) &&
        shouldRefreshCatalogOnlineUsers(
          autoReload,
          isInitialFetch,
          getDocumentVisibilityState(),
        )
      ) {
        void refreshOnlineUsers(queryClient);
      }
      const catalog = await catalogPromise;
      lastFetchContextRef.current = fetchContext;
      return {
        pages: [catalog],
        pageParams: [1],
      };
    },
    select: mergePages,
    ...createCatalogAutoReloadQueryBehavior(autoReload, () => visibilityState),
    enabled: options.enabled ?? true,
  });

  useEffect(
    () => (): void => {
      void queryClient.cancelQueries({ queryKey });
    },
    [queryClient, queryKey],
  );

  const fetchNextPage = useCallback(async (): Promise<void> => {
    if (isFetchingNextPageRef.current) return;
    const current =
      queryClient.getQueryData<InfiniteData<Catalog, number>>(queryKey);
    const lastPage = current?.pages.at(-1);
    if (!current || !lastPage?.pagination.hasNextPage) return;

    const nextPage = lastPage.pagination.page + 1;
    isFetchingNextPageRef.current = true;
    setIsFetchingNextPage(true);
    setNextPageError(null);
    try {
      const pageQueryKey = [...queryKey, "page", nextPage] as const;
      let nextCatalog: Catalog;
      try {
        nextCatalog = await queryClient.fetchQuery({
          queryKey: pageQueryKey,
          queryFn: ({ signal }: { signal: AbortSignal }): Promise<Catalog> =>
            fetchPage(nextPage, signal),
          staleTime: 0,
          gcTime: 0,
        });
      } finally {
        queryClient.removeQueries({ queryKey: pageQueryKey, exact: true });
      }
      queryClient.setQueryData<InfiniteData<Catalog, number>>(
        queryKey,
        (latest) => {
          const latestLastPage = latest?.pages.at(-1);
          if (
            !latest ||
            !latestLastPage?.pagination.hasNextPage ||
            latestLastPage.pagination.page + 1 !== nextPage
          ) {
            return latest;
          }
          return {
            pages: [...latest.pages, nextCatalog],
            pageParams: [...latest.pageParams, nextPage],
          };
        },
      );
    } catch (error) {
      if (!isCancelledError(error)) {
        setNextPageError(
          getApiErrorMessage(error, "カタログの続きを読み込めませんでした"),
        );
      }
    } finally {
      isFetchingNextPageRef.current = false;
      setIsFetchingNextPage(false);
    }
  }, [fetchPage, queryClient, queryKey]);

  useAimogeCatalogSnapshot(options.enabled === false ? undefined : query.data);

  return {
    ...query,
    fetchNextPage,
    hasNextPage: query.data?.pagination.hasNextPage ?? false,
    isFetching: query.isFetching || isFetchingNextPage,
    isFetchingNextPage,
    nextPageError,
  };
}
